import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal } from "react-bootstrap";
import api from "../../../../Services/api";
import FormHandler from "../../Common/Form/FormHandler/FormHandler";
import { toast } from "react-toastify";
import { useKeycloak } from "@react-keycloak/web";

import {
  keycloakbaseurl,
  createRole,
  updateRole,
  createRoleintoRealm,
  deletePermissionsFromRole,
  getAllPermissions,
  getRoleDataByRoleNameURL,
  addPermissionsToRole,
  getThemeURL, // ADD THIS IMPORT
} from "../../../../Services/EndPoints";
import {
  CreateRoleConfig,
  schema,
} from "../../../../JsonData/CreateRoleConfig";
import {
  postDataWithBody,
  getData,
  putDataWithBody,
} from "../../../../Services/Services";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";

// Component for creating and editing role data
const RoleRegistrationForm = () => {
  // Keycloak context for auth
  const { keycloak } = useKeycloak();
  const [roleConfig, setRoleConfig] = useState(CreateRoleConfig);
  // ADD State for forcing FormHandler re-mount
  const [formKey, setFormKey] = useState(Date.now());

  const [initDataLoadComplete, setInitDataLoadComplete] = useState(false);
  const [permissionsChanged, isPermissionsChanged] = useState(false);
  const [rolePermissionData, setRolePermissionData] = useState([]);
  const [isFieldDisabled, setIsFieldDisabled] = useState({
    web_permission: false,
    Web_access: false,
  });
  const [activeTab, setActiveTab] = useState(0);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // ADD: State for Super Admin confirmation modal and user profile
  const [showSuperAdminModal, setShowSuperAdminModal] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  const navigate = useNavigate();
  const { state } = useLocation();
  let dataFrEdit = state?.data;
  let edit = state?.edit;
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // ADD: State to track if current user is admin
  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(false);

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema[activeTab]),
    mode: "all",
    criteriaMode: "all",
  });

  // Watch for changes in user access type
  const userAccessType = watch("User Acess Type", { web: true });

  const [selectedModules, setSelectedModules] = useState([]);

  // Fixed useEffect for handling userAccessType changes
  useEffect(() => {
    // Ensure we have a proper object structure
    const accessType = userAccessType || {};
    const hasWeb = Boolean(accessType.web);

    // Update field disabled states based on access type
    setIsFieldDisabled({
      web_permission: !hasWeb,
      Web_access: !hasWeb,
    });

    // Clear fields when access type is disabled
    if (!hasWeb) {
      setValue("permission", "");
      setValue("Web_access", {
        view_permission: false,
        edit_permission: false,
        delete_permission: false,
      });
    }
  }, [userAccessType, setValue]);

  useEffect(() => {
    getInitFormData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  console.log(selectedModules, "selectedModules");

  const getUserProfile = async () => {
    try {
      const userProfileData = await getData(getThemeURL);
      if (userProfileData && userProfileData.length > 0) {
        const profile = userProfileData[0];
        console.log("User profile loaded:", profile);
        setUserProfile(profile);

        // ADD: Check and handle admin user
        if (profile && profile.is_admin === true) {
          console.log("Current user is admin, disabling admin radio button");
          setIsCurrentUserAdmin(true);

          // Auto-select "Users" option
          setValue("Admin_Access", "false", { shouldValidate: true });

          // Update the form configuration - only disable the admin option, not the entire field
          const updatedConfig = [...roleConfig];
          updatedConfig.forEach((tab) => {
            tab.fields = tab.fields.map((field) => {
              if (field.name === "Admin_Access") {
                return {
                  ...field,
                  // Instead of disabling the entire field, modify the options
                  options:
                    field.options?.map((option) =>
                      option.value === "true"
                        ? { ...option, disabled: true } // Disable only admin option
                        : option
                    ) || field.options,
                };
              }
              return field;
            });
          });
          setRoleConfig(updatedConfig);
        } else {
          setIsCurrentUserAdmin(false);
        }

        return profile;
      }
      return null;
    } catch (error) {
      console.error("Error fetching user profile:", error);
      toast.error("Failed to fetch user profile", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      return null;
    }
  };

  // Fetch all user permissions and populate form during edit
  const getInitFormData = async () => {
    await getAllUserPermission();
    await getUserProfile(); // ADD: Fetch user profile on init
    setInitDataLoadComplete(true);
    if (dataFrEdit) {
      populatePermissionDataFrEdit(dataFrEdit.role_permission);
    }
  };

  // ADD: Debug useEffect to monitor userProfile changes
  useEffect(() => {
    console.log("User profile updated:", userProfile);
  }, [userProfile]);

  // Replace the useEffect around line 125-155 in RoleRegistrationForm.jsx
  useEffect(() => {
    if (initDataLoadComplete && dataFrEdit) {
      // First set basic form values
      setValue("name", dataFrEdit.name);
      setValue("description", dataFrEdit.description);

      // Set user access types - only web
      setValue("User Acess Type", { web: true });

      // Set web access permissions
      if (dataFrEdit.webPermissions) {
        const webPermissions = dataFrEdit.webPermissions.split(",");
        setValue("Web_access", {
          view_permission: webPermissions.includes("V"),
          edit_permission: webPermissions.includes("E"),
          delete_permission: webPermissions.includes("D"),
        });
      }

      // FIXED: Set selectedModules and permissions with proper labels
      if (dataFrEdit.role_permission && dataFrEdit.role_permission.length > 0) {
        const webPermissions = dataFrEdit.role_permission.filter(
          (perm) => perm.userAccessType === "W" || !perm.userAccessType
        );

        if (webPermissions.length > 0) {
          // Get permission hierarchy for proper labels
          const permissionHierarchy =
            CreateRoleConfig[0].fields[2].options || [];

          // IMPROVED: Function to find module with proper label
          const findModuleInHierarchy = (hierarchy, targetId) => {
            for (const item of hierarchy) {
              if (item.id === targetId) {
                const displayLabel =
                  item.label || item.name || `Module ${item.id}`;
                return {
                  id: item.id,
                  label: displayLabel,
                  value: item.value || item.id,
                };
              }
              if (item.children && item.children.length > 0) {
                const found = findModuleInHierarchy(item.children, targetId);
                if (found) return found;
              }
            }
            return null;
          };

          // Build modules with proper labels
          const modulesForGrid = webPermissions.map((perm) => {
            const moduleInfo = findModuleInHierarchy(
              permissionHierarchy,
              perm.permission_id
            );
            if (moduleInfo) {
              return {
                id: moduleInfo.id,
                label: moduleInfo.label,
                name: moduleInfo.name || moduleInfo.label, // ADD: Include name
                value: moduleInfo.value || moduleInfo.id,
                _children: moduleInfo._children || [], // ADD: Include _children if exists
              };
            } else {
              // Fallback without "Permission" prefix
              return {
                id: perm.permission_id,
                label: `Module ${perm.permission_id}`,
                name: `Module ${perm.permission_id}`, // ADD: Include name
                value: perm.permission_id,
              };
            }
          });

          console.log(
            "Setting selectedModules from edit data:",
            modulesForGrid
          );
          setSelectedModules(modulesForGrid);

          // Set permissions AFTER selectedModules with a delay
          setTimeout(() => {
            const modulePermissionsForEdit = {};
            webPermissions.forEach((perm) => {
              const webPerms = perm.webPermissions
                ? perm.webPermissions.split(",")
                : [];
              modulePermissionsForEdit[perm.permission_id] = {
                view: webPerms.includes("V"),
                edit: webPerms.includes("E"),
                delete: webPerms.includes("D"),
              };
            });

            console.log(
              "Setting module permissions for edit (delayed):",
              modulePermissionsForEdit
            );
            setValue("modulePermissions", modulePermissionsForEdit);
          }, 200);
        }
      }
    }
  }, [initDataLoadComplete, dataFrEdit, setValue]);

  // UPDATED: Helper to map backend permission data to UI state for edit
  const populatePermissionDataFrEdit = (permissionData) => {
    console.log("=== populatePermissionDataFrEdit DEBUG ===");
    console.log("permissionData", permissionData);

    if (!permissionData || permissionData.length === 0) return;

    // Only handle web permissions
    const webPermissions = permissionData.filter(
      (perm) => perm.userAccessType === "W" || !perm.userAccessType
    );

    // Handle web permissions
    if (webPermissions.length > 0) {
      const webPermissionIds = webPermissions.map(
        (permission) => permission.permission_id
      );

      console.log("webPermissionIds from backend:", webPermissionIds);

      setRolePermissionData(
        webPermissions.map((perm) => ({
          permission_id: perm.permission_id,
        }))
      );

      // Get the permissions hierarchy from the config
      const permissionHierarchy = CreateRoleConfig[0].fields[2].options || [];

      // Function to find module details from hierarchy
      const findModuleInHierarchy = (hierarchy, targetId) => {
        for (const item of hierarchy) {
          if (item.id === targetId) {
            console.log("Found item for ID", targetId, ":", item);
            const displayLabel = item.label || item.name || `Module ${item.id}`;
            return {
              id: item.id,
              label: displayLabel,
              value: item.value || item.id,
              parent_id: item.parent_id,
            };
          }
          if (item.children && item.children.length > 0) {
            const found = findModuleInHierarchy(item.children, targetId);
            if (found) return found;
          }
        }
        return null;
      };

      // Build selectedModules array
      // Build selectedModules array
      const modulesForGrid = [];
      webPermissionIds.forEach((permId) => {
        const moduleInfo = findModuleInHierarchy(permissionHierarchy, permId);
        if (moduleInfo) {
          modulesForGrid.push({
            id: moduleInfo.id,
            label: moduleInfo.label,
            name: moduleInfo.name || moduleInfo.label, // ADD: Include name
            value: moduleInfo.value,
            parent_id: moduleInfo.parent_id,
            _children: moduleInfo._children || [], // ADD: Include _children if exists
          });
          console.log(
            `Found module for permission ${permId}:`,
            moduleInfo.label
          );
        } else {
          modulesForGrid.push({
            id: permId,
            label: `Module ${permId}`,
            name: `Module ${permId}`, // ADD: Include name
            value: permId,
          });
          console.log(`Created fallback module for permission ${permId}`);
        }
      });

      console.log("Setting selectedModules for edit mode:", modulesForGrid);
      setSelectedModules(modulesForGrid);

      // UPDATED: Mark nodes as selected in the tree
      let permissionDataTemp = CreateRoleConfig[0].fields[2].options;

      // Recursive function to add selected flags - checks node.id, children, and _children
      const addSelectedFlagSmart = (data, selectedValues) => {
        return data.map((node) => {
          const o = { ...node };

          console.log(`\nChecking node: ${o.label} (ID: ${o.id})`);

          // Check if this node itself is selected
          if (selectedValues.includes(o.id)) {
            o.checked = true;
            console.log(
              `  ✓ Node ${o.id} (${o.label}) marked as checked - direct match`
            );
          }
          // Check if this node has _children (array of IDs) and ALL are selected
          else if (o._children && o._children.length > 0) {
            const allChildrenSelected = o._children.every((childId) =>
              selectedValues.includes(childId)
            );

            console.log(`  - Node has _children:`, o._children);
            console.log(`  - All _children selected:`, allChildrenSelected);

            if (allChildrenSelected) {
              o.checked = true;
              console.log(
                `  ✓ Node ${o.id} (${o.label}) marked as checked - all _children selected`
              );
            }
          }
          // Check if this node has children (array of objects) and ALL are selected
          else if (o.children && o.children.length > 0) {
            const childIds = o.children.map((child) => child.id);
            const allChildrenSelected = childIds.every((childId) =>
              selectedValues.includes(childId)
            );

            console.log(
              `  - Node has children (${childIds.length}):`,
              childIds
            );
            console.log(`  - All children selected:`, allChildrenSelected);

            if (allChildrenSelected) {
              o.checked = true;
              console.log(
                `  ✓ Node ${o.id} (${o.label}) marked as checked - all children selected`
              );
            }
          }

          // Recursively process children
          if (o.children && o.children.length > 0) {
            o.children = addSelectedFlagSmart(o.children, selectedValues);
          }

          return o;
        });
      };

      console.log("\n=== Starting tree selection marking ===");
      console.log("webPermissionIds for tree selection:", webPermissionIds);

      let permissionDataWthSlctdValus = addSelectedFlagSmart(
        permissionDataTemp,
        webPermissionIds
      );

      console.log(
        "Updated permission data with selected values:",
        permissionDataWthSlctdValus
      );

      const updatedConfig = [...CreateRoleConfig];
      updatedConfig[0].fields[2]["options"] = permissionDataWthSlctdValus;
      setRoleConfig(updatedConfig);

      const permissionString = webPermissionIds.join(",");
      setValue("permission", permissionString);
      setValue("web_permission", permissionString);

      // Set up module permissions for edit mode
      const modulePermissionsForEdit = {};
      webPermissions.forEach((perm) => {
        const webPerms = perm.webPermissions
          ? perm.webPermissions.split(",")
          : [];
        modulePermissionsForEdit[perm.permission_id] = {
          view: webPerms.includes("V"),
          edit: webPerms.includes("E"),
          delete: webPerms.includes("D"),
        };
      });

      console.log(
        "Setting module permissions for edit:",
        modulePermissionsForEdit
      );
      setValue("modulePermissions", modulePermissionsForEdit);
    }

    if (dataFrEdit.webPermissions) {
      const webPerms = dataFrEdit.webPermissions.split(",");
      setValue("Web_access", {
        view_permission: webPerms.includes("V"),
        edit_permission: webPerms.includes("E"),
        delete_permission: webPerms.includes("D"),
      });
    }

    // Handle admin access
    if (dataFrEdit && dataFrEdit.hasOwnProperty("is_admin")) {
      const adminValue = String(dataFrEdit.is_admin === true);
      setValue("Admin_Access", adminValue);

      setTimeout(() => {
        setValue("Admin_Access", adminValue, { shouldValidate: true });
        trigger("Admin_Access");
      }, 200);
    }

    console.log("=== END populatePermissionDataFrEdit DEBUG ===");
  };

  // // Mark selected nodes in permissions hierarchy
  // function addSelectedFlag(data, selectedValues) {
  //   return data.map(({ type, children = [], ...rest }) => {
  //     const o = { ...rest, type };
  //     if (selectedValues.indexOf(o.id) > -1) {
  //       o.checked = true;
  //     }
  //     if (children.length)
  //       o.children = addSelectedFlag(children, selectedValues);
  //     return o;
  //   });
  // }

  // Fetch all user permissions and format into hierarchy
  async function getAllUserPermission() {
    let permissionData = await getData(getAllPermissions);
    let permissionDataSanitized = flatToHier(permissionData);
    CreateRoleConfig[0].fields[2]["options"] = permissionDataSanitized;
  }

  function flatToHier(flat) {
    var roots = []; // things without parent
    var all = {};
    flat.forEach(function (item) {
      item["label"] = item.name;
      item["value"] = item.id;
      all[item.id] = item;
    });
    Object.keys(all).forEach(function (id) {
      var item = all[id];
      if (item.parent_id === null) {
        roots.push(item);
      } else if (item.parent_id in all) {
        var p = all[item.parent_id];
        if (!("children" in p)) {
          p.children = [];
        }
        p.children.push(item);
      }
    });
    return roots;
  }

  // Handle selection on permission tree dropdown
  const onHierDropdownChange = (currentNode, selectedNodes) => {
    console.log("=== onHierDropdownChange DEBUG ===");
    console.log("currentNode:", currentNode);
    console.log("selectedNodes:", selectedNodes);

    isPermissionsChanged(true);
    let permissionArr = [];
    let modulesForGrid = [];

    // Get the full permission hierarchy to look up child details
    const permissionHierarchy = CreateRoleConfig[0]?.fields[2]?.options || [];

    // Helper function to find a node by ID in the hierarchy
    const findNodeById = (hierarchy, targetId) => {
      for (const node of hierarchy) {
        if (node.id === targetId) {
          return node;
        }
        if (node.children && node.children.length > 0) {
          const found = findNodeById(node.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    selectedNodes.forEach(function (selectedNode) {
      console.log("Processing selectedNode:", selectedNode);

      // Check if node has _children (array of child IDs)
      if (selectedNode._children && selectedNode._children.length > 0) {
        console.log("Node has _children:", selectedNode._children);

        // Add each child to the arrays
        selectedNode._children.forEach((childId) => {
          // Find the full child node details from hierarchy
          const childNode = findNodeById(permissionHierarchy, childId);

          if (childNode) {
            permissionArr.push({ permission_id: childId });

            modulesForGrid.push({
              id: childId,
              label: childNode.label || childNode.name || `Module ${childId}`,
              name: childNode.name || childNode.label, // ADD: Include name property
              value: childNode.value || childId,
              _children: childNode._children || [], // ADD: Include _children if exists
            });

            console.log(
              `Added child ${childId}:`,
              childNode.label || childNode.name
            );
          } else {
            // Fallback if child not found in hierarchy
            permissionArr.push({ permission_id: childId });

            modulesForGrid.push({
              id: childId,
              label: `Module ${childId}`,
              name: `Module ${childId}`, // ADD: Include name property
              value: childId,
            });

            console.log(`Added child ${childId} (fallback)`);
          }
        });
      } else {
        // If no _children, add the node itself (it's a leaf node)
        console.log("Node has no _children, adding itself");

        permissionArr.push({ permission_id: selectedNode.id });

        modulesForGrid.push({
          id: selectedNode.id,
          label: selectedNode.label || selectedNode.name,
          name: selectedNode.name || selectedNode.label, // ADD: Include name property
          value: selectedNode.value || selectedNode.id,
          _children: selectedNode._children || [], // ADD: Include _children if exists
        });
      }
    });

    console.log("permissionArr before unique:", permissionArr);
    console.log("modulesForGrid before unique:", modulesForGrid);

    // Remove duplicates from permissionArr
    const uniquePermissions = Array.from(
      new Set(permissionArr.map((p) => p.permission_id))
    ).map((id) => ({ permission_id: id }));

    // Remove duplicates from modulesForGrid based on id
    const uniqueModules = modulesForGrid.filter(
      (module, index, self) =>
        index === self.findIndex((m) => m.id === module.id)
    );

    console.log("uniquePermissions:", uniquePermissions);
    console.log("uniqueModules:", uniqueModules);

    setRolePermissionData(uniquePermissions);
    setSelectedModules(uniqueModules);

    const permissionString = uniquePermissions
      .map((item) => item.permission_id)
      .join(",");

    console.log("permissionString:", permissionString);

    // Set both field names to ensure validation passes
    setValue("permission", permissionString);
    setValue("web_permission", permissionString);

    // Trigger validation for both fields
    trigger("permission");
    trigger("web_permission");

    console.log("=== END onHierDropdownChange DEBUG ===");
  };

  // Generate the Form Handler UI for fields
  const GenerateForm = (fields, register, errors, control) => {
    return (
      <div>
        {initDataLoadComplete === true && (
          <FormHandler
            key={formKey}
            register={register}
            errors={errors}
            fields={fields}
            onChange={onHierDropdownChange}
            control={control}
            columns={2}
            getValues={getValues}
            setValue={setValue}
            webaccess={true}
            edit={edit}
            isCurrentUserAdmin={isCurrentUserAdmin}
          />
        )}
      </div>
    );
  };

  // Add this helper function near the top of RoleRegistrationForm component
  const getGISMappingModuleIds = (selectedModules) => {
    const gisModuleIds = new Set();

    console.log("=== getGISMappingModuleIds DEBUG ===");
    console.log("Input selectedModules:", selectedModules);

    selectedModules.forEach((module) => {
      console.log("Checking module:", module);

      // Check if the module label or name contains "GIS Mapping" or has id 12
      const isGISByName = module.label && module.label.includes("GIS Mapping");
      const isGISByNameField =
        module.name && module.name.includes("GIS Mapping");
      const isGISById = module.id === 12;

      console.log(`Module ${module.id} checks:`, {
        label: module.label,
        name: module.name,
        isGISByName,
        isGISByNameField,
        isGISById,
      });

      if (isGISByName || isGISByNameField || isGISById) {
        console.log(`✓ Module ${module.id} is GIS Mapping - adding to set`);
        gisModuleIds.add(module.id);

        // Also add any children if they exist
        if (module._children && Array.isArray(module._children)) {
          console.log(`  Adding _children:`, module._children);
          module._children.forEach((childId) => gisModuleIds.add(childId));
        }
      }
    });

    const result = Array.from(gisModuleIds);
    console.log("Final gisModuleIds:", result);
    console.log("=== END getGISMappingModuleIds DEBUG ===");

    return result;
  };

  // In your main component - update generateTabsData
  const generateTabsData = (tabWiseData) => {
    tabWiseData?.forEach((element) => {
      // Calculate GIS mapping module IDs
      const gisModuleIds = getGISMappingModuleIds(selectedModules);
      // Update module_permissions_section field with selectedModules and edit flag
      element.fields = element.fields.map((field) => {
        if (field.type === "module_permissions_grid") {
          return {
            ...field,
            selectedModules: selectedModules,
            edit: edit,
            gisModuleIds: gisModuleIds,
          };
        }
        return field;
      });

      return (element.component = GenerateForm(
        element?.fields,
        register,
        errors,
        control
      ));
    });
    return tabWiseData;
  };

  const getLoggedInUserAuthToken = async () => {
    return keycloak.token;
  };

  const getURMHeader = async () => {
    const authToknObj = await getLoggedInUserAuthToken();
    let headers = {
      "content-type": "application/json",
      Authorization: "Bearer " + authToknObj,
    };
    return headers;
  };

  // Fetch role data by role name (from Keycloak)
  const getRoleDataByRoleName = async (roleName) => {
    let url =
      keycloakbaseurl +
      getRoleDataByRoleNameURL.replace(
        "{REALM}",
        process.env.REACT_APP_URM_REALM
      ) +
      roleName;
    let headers = await getURMHeader();
    const response = await api.getData(url, null, headers);
    return response.data;
  };

  const createRoleInApp = async (input) => {
    input["status"] = true;
    input["adminRole"] = input.adminRole || "SA";
    input["bankId"] = input.bankId || null;
    delete input["attributes"];
    return await postDataWithBody(createRole, input, {});
  };

  const updateRoleInApp = async (input) => {
    input["status"] = true;
    input["adminRole"] = input.adminRole || "SA";
    input["bankId"] = input.bankId || null;
    delete input["attributes"];
    return await putDataWithBody(updateRole, input, {});
  };

  const showSuccessMsg = (msg) => {
    toast.success(msg, {
      position: toast.POSITION.TOP_CENTER,
      autoClose: 2000,
    });
  };

  const showErrorMsg = (msg) => {
    toast.error(msg, {
      position: toast.POSITION.TOP_CENTER,
      autoClose: 2000,
    });
  };

  const cleanModulePermissionsData = (modulePermissions) => {
    if (!modulePermissions) return {};

    console.log("=== CLEAN MODULE PERMISSIONS DEBUG ===");
    console.log("Original modulePermissions:", modulePermissions);
    console.log("Type of modulePermissions:", typeof modulePermissions);

    if (
      modulePermissions &&
      typeof modulePermissions === "object" &&
      !Array.isArray(modulePermissions)
    ) {
      const cleanedPermissions = {};
      Object.keys(modulePermissions).forEach((permissionId) => {
        const perms = modulePermissions[permissionId];
        if (perms && typeof perms === "object") {
          const numericPermissionId = parseInt(permissionId);
          cleanedPermissions[numericPermissionId] = {
            view: perms.view === true,
            edit: perms.edit === true,
            delete: perms.delete === true,
          };
          console.log(
            `Permission ID ${numericPermissionId}:`,
            cleanedPermissions[numericPermissionId]
          );
        }
      });
      console.log("Final cleaned permissions:", cleanedPermissions);
      console.log("=== END CLEAN DEBUG ===");
      return cleanedPermissions;
    }

    console.log("No valid modulePermissions found");
    console.log("=== END CLEAN DEBUG ===");
    return {};
  };

  // MODIFIED: Main submit handler with Super Admin check
  const submitHandler = async (data, e) => {
    console.log("=== SUBMIT HANDLER CALLED ===");
    console.log("User Profile:", userProfile);
    console.log("Edit Mode:", edit);
    console.log("User Role:", userProfile?.role_name);

    e.preventDefault();

    // Check if user is SUPER ADMIN and in edit mode
    if (edit && userProfile && userProfile.role_name === "SUPER ADMIN") {
      console.log("Showing Super Admin confirmation modal");
      // Show confirmation modal
      setPendingSubmitData({ data, e });
      setShowSuperAdminModal(true);
      return;
    }

    console.log(
      "Proceeding with normal submit (non-Super Admin or create mode)"
    );
    // If not Super Admin or not in edit mode, proceed normally
    await processSubmit(data, e);
  };

  // ADD: Handler for Super Admin modal confirmation
  const handleSuperAdminConfirm = async () => {
    setShowSuperAdminModal(false);
    if (pendingSubmitData) {
      await processSubmit(pendingSubmitData.data, pendingSubmitData.e);
      setPendingSubmitData(null);
    }
  };

  // ADD: Handler for Super Admin modal cancel
  const handleSuperAdminCancel = () => {
    setShowSuperAdminModal(false);
    setPendingSubmitData(null);
    setIsSubmitting(false);
  };

  // RENAMED: Original submit logic moved to processSubmit
  const processSubmit = async (data, e) => {
    console.log("=== PROCESS SUBMIT DEBUG ===");
    console.log("Raw Form Data:", data);
    console.log("Selected Modules:", selectedModules);
    console.log("Role Permission Data:", rolePermissionData);
    console.log("Module Permissions Raw:", data.modulePermissions);

    if (isCurrentUserAdmin) {
      console.log("Enforcing is_admin = false for admin user");
      data.Admin_Access = "false";
    }

    const cleanedModulePermissions = cleanModulePermissionsData(
      data.modulePermissions
    );

    console.log("Cleaned Module Permissions:", cleanedModulePermissions);

    const isValid = await trigger();
    if (!isValid) {
      console.log("Form validation failed:", errors);
      // Show all validation errors
      Object.keys(errors).forEach((field) => {
        console.log(`Validation error for ${field}:`, errors[field]);
      });
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(true);

    let userAccessType = "W";

    const modulePermissions = cleanedModulePermissions;

    let webPermissions = [];
    if (data.Web_access?.view_permission) webPermissions.push("V");
    if (data.Web_access?.edit_permission) webPermissions.push("E");
    if (data.Web_access?.delete_permission) webPermissions.push("D");
    webPermissions = webPermissions.join(",");

    let rolePermissionsWithAccess = [];

    rolePermissionData.forEach((perm) => {
      console.log(`=== PROCESSING PERMISSION ${perm.permission_id} ===`);

      let moduleAccess = null;

      moduleAccess = modulePermissions[perm.permission_id];

      if (!moduleAccess) {
        moduleAccess = modulePermissions[perm.permission_id.toString()];
      }

      if (!moduleAccess) {
        moduleAccess = modulePermissions[parseInt(perm.permission_id)];
      }

      console.log(`Permission ID ${perm.permission_id}:`, moduleAccess);
      console.log(
        `Available keys in modulePermissions:`,
        Object.keys(modulePermissions)
      );
      console.log(`Permission ID type:`, typeof perm.permission_id);

      if (moduleAccess && typeof moduleAccess === "object") {
        let accessString = [];
        if (moduleAccess.view === true) accessString.push("V");
        if (moduleAccess.edit === true) accessString.push("E");
        if (moduleAccess.delete === true) accessString.push("D");

        const webPermissionString =
          accessString.length > 0 ? accessString.join(",") : "V";

        console.log(
          `Permission ID ${perm.permission_id} has moduleAccess:`,
          moduleAccess
        );
        console.log(
          `Permission ID ${perm.permission_id} gets: ${webPermissionString}`
        );

        rolePermissionsWithAccess.push({
          permission_id: perm.permission_id,
          userAccessType: "W",
          webPermissions: webPermissionString,
        });
      } else {
        const hasAnyModulePermissions =
          Object.keys(modulePermissions).length > 0;
        const defaultPermissions = hasAnyModulePermissions ? "V" : "V,E,D";

        console.log(`Permission ID ${perm.permission_id} has no moduleAccess`);
        console.log(`Has any module permissions: ${hasAnyModulePermissions}`);
        console.log(`Defaulting to: ${defaultPermissions}`);

        rolePermissionsWithAccess.push({
          permission_id: perm.permission_id,
          userAccessType: "W",
          webPermissions: defaultPermissions,
        });
      }
      console.log(`=== END PROCESSING PERMISSION ${perm.permission_id} ===`);
    });

    console.log("Final rolePermissionsWithAccess:", rolePermissionsWithAccess);
    console.log("=== END PROCESS SUBMIT DEBUG ===");

    const isAdmin = isCurrentUserAdmin ? false : data.Admin_Access === "true";

    let inputobj = {
      name: data.name,
      description: data.description,
      attributes: {
        created_on: [new Date().getTime()],
      },
      userAccessType: userAccessType,
      webPermissions: webPermissions,
      is_admin: isAdmin,
      adminRole: isAdmin ? "HO" : "U",
      bankId: userProfile.bank_id || null,
      urm_role_id: data.urm_role_id || null,
      appType: userProfile.app_type || null, // e.g. "FLEET" — from logged-in user's profile
    };

    const permissionString = getValues("permission");

    if (permissionString) {
      const permissionIds = permissionString
        .split(",")
        .filter((id) => id !== "");
      const updatedPermissions = permissionIds.map((id) => {
        const moduleAccess =
          modulePermissions[id] ||
          modulePermissions[id.toString()] ||
          modulePermissions[parseInt(id)];
        let accessString = [];

        if (moduleAccess) {
          if (moduleAccess.view === true) accessString.push("V");
          if (moduleAccess.edit === true) accessString.push("E");
          if (moduleAccess.delete === true) accessString.push("D");
        }

        const webPermissionString =
          accessString.length > 0 ? accessString.join(",") : "V";

        return {
          permission_id: parseInt(id),
          userAccessType: "W",
          webPermissions: webPermissionString,
        };
      });
      setRolePermissionData(updatedPermissions);
    }

    if (dataFrEdit) {
      inputobj["id"] = dataFrEdit.id;

      if (!permissionsChanged && permissionString) {
        const permissionIds = permissionString
          .split(",")
          .filter((id) => id !== "");
        if (permissionIds.length > 0) {
          const updatedPermissions = permissionIds.map((id) => {
            const moduleAccess =
              modulePermissions[id] ||
              modulePermissions[id.toString()] ||
              modulePermissions[parseInt(id)];
            let accessString = [];

            if (moduleAccess) {
              if (moduleAccess.view === true) accessString.push("V");
              if (moduleAccess.edit === true) accessString.push("E");
              if (moduleAccess.delete === true) accessString.push("D");
            }

            const webPermissionString =
              accessString.length > 0 ? accessString.join(",") : "V";

            return {
              permission_id: parseInt(id),
              userAccessType: "W",
              webPermissions: webPermissionString,
            };
          });
          setRolePermissionData(updatedPermissions);
          isPermissionsChanged(true);
        }
      }

      if (rolePermissionData.length === 0 && dataFrEdit.role_permission) {
        setRolePermissionData(
          dataFrEdit.role_permission.map((perm) => ({
            permission_id: perm.permission_id,
            userAccessType: "W",
            webPermissions: perm.webPermissions || "V,E,D",
          }))
        );
      }

      let res = await putDataWithBody(
        deletePermissionsFromRole + dataFrEdit.id,
        {},
        {}
      );

      if (res.status === "success") {
        res = await updateRoleInApp(inputobj);
        if (res.id) {
          const permissionsToAssign =
            rolePermissionsWithAccess.length > 0
              ? rolePermissionsWithAccess
              : [...rolePermissionData];
          await assignPermissionsToRoleWithData(
            dataFrEdit.id,
            permissionsToAssign
          );
        }
      }
    } else {
      let url =
        keycloakbaseurl +
        createRoleintoRealm.replace("{REALM}", process.env.REACT_APP_URM_REALM);
      let headers = await getURMHeader();

      try {
        const response = await api.postData(
          url,
          headers,
          JSON.stringify({
            name: inputobj.name,
            description: inputobj.description,
            attributes: inputobj.attributes,
          })
        );

        if (response.status === 201) {
          let roleobj = await getRoleDataByRoleName(data.name);
          inputobj["urm_role_id"] = roleobj.id;
          let res = await createRoleInApp(inputobj);
          if (res.id) {
            await assignPermissionsToRoleWithData(
              res.id,
              rolePermissionsWithAccess
            );
          }
        }
        console.log(isFieldDisabled, "isFieldDisabled");
      } catch (e) {
        if (e.response?.data?.errorMessage?.includes("already exists")) {
          // Show your custom toast message
          showErrorMsg(
            "Role name already exists. Please choose a different name."
          );
        } else {
          showErrorMsg(e.response?.data?.errorMessage || "An error occurred");
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const assignPermissionsToRoleWithData = async (
    roleId,
    webPermissionsData
  ) => {
    let formattedPermissions = [];

    if (webPermissionsData && webPermissionsData.length > 0) {
      const webPermissions = webPermissionsData.map((perm) => ({
        permission_id: perm.permission_id,
        userAccessType: "W",
        webPermissions: perm.webPermissions,
      }));
      formattedPermissions = [...webPermissions];
    }

    let roleObj = {
      id: roleId,
      role_permission: formattedPermissions,
    };

    console.log("Final roleObj being sent to API:", roleObj);

    let res = await postDataWithBody(
      addPermissionsToRole + roleId,
      roleObj,
      {}
    );

    if (res.status === "success") {
      showSuccessMsg(
        "Role " + (dataFrEdit ? "updated" : "created") + " successfully."
      );
      navigate("../roles");
    } else {
      showErrorMsg(
        "An error occurred while " +
          (dataFrEdit ? "updating" : "creating") +
          " the role."
      );
    }
  };

  const saveTabDataHandler = async (e) => {
    const isStepValid = await trigger();
    if (isStepValid) setActiveTab((prevId) => prevId + 1);
  };

  const previousHandler = () => {
    if (activeTab > 0) {
      setActiveTab((prevId) => prevId - 1);
    }
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleConfirmCancel = () => {
    // Reset ALL form values comprehensively
    reset({
      name: "",
      description: "",
      web_permission: "",
      permission: "",
      "User Acess Type": { web: true },
      Web_access: {
        view_permission: false,
        edit_permission: false,
        delete_permission: false,
      },
      modulePermissions: {},
      Admin_Access: isCurrentUserAdmin ? "false" : "",
    });

    // Clear all related state
    setSelectedModules([]);
    setRolePermissionData([]);
    isPermissionsChanged(false);

    // Reset roleConfig permissions
    const resetPermissions = (nodes) => {
      if (!Array.isArray(nodes)) return nodes;

      return nodes.map((node) => {
        if (!node) return node;

        return {
          ...node,
          checked: false,
          selected: false, // Clear selection state
          children: node.children
            ? resetPermissions(node.children)
            : node.children,
        };
      });
    };

    // Create a new config based on the current roleConfig structure
    const newRoleConfig = roleConfig.map((section) => ({
      ...section,
      fields: section.fields.map((field) => {
        // Handle both 'web_permission' and 'permission' field names
        if (field.name === "web_permission" || field.name === "permission") {
          return {
            ...field,
            options: field.options
              ? resetPermissions(field.options)
              : field.options,
            selectedValue: "", // Clear any selected display value
            value: "", // Clear the field value
          };
        }
        return field;
      }),
    }));

    setRoleConfig(newRoleConfig);

    // Force clear both form fields
    setValue("web_permission", "", {
      shouldValidate: false,
      shouldDirty: false,
    });
    setValue("permission", "", { shouldValidate: false, shouldDirty: false });

    // FORCE FormHandler component to completely re-mount by changing key
    setFormKey(Date.now());

    // Force re-trigger validation to clear any error states
    setTimeout(() => {
      trigger();
    }, 100);

    // Close modal
    setShowCancelModal(false);
  };

  const tabsData = generateTabsData(roleConfig);

  return (
    <div className="mt-4">
      {/* ADD: Debug error display (remove after debugging) */}
      {/* {Object.keys(errors).length > 0 && (
        <div style={{ background: 'red', color: 'white', padding: '10px', margin: '10px' }}>
          <h4>Form Validation Errors:</h4>
          {Object.keys(errors).map(field => (
            <div key={field}>
              <strong>{field}:</strong> {errors[field]?.message || 'Unknown error'}
            </div>
          ))}
        </div>
      )} */}

      <div className="form-body">
        <div className="registration__form--body">
          <form onSubmit={handleSubmit(submitHandler)} name="AgancyRegForm">
            <div>
              {tabsData?.map((tab) => (
                <React.Fragment key={tab.id}>
                  {tab.id === activeTab && (
                    <>
                      <div className="registration__form--heading">
                        {tab.heading}
                      </div>
                      <div>{tab.component}</div>
                    </>
                  )}
                </React.Fragment>
              ))}
            </div>
            <div
              className="form-submit-container full-width-field"
              style={{ marginTop: "50px" }}
            >
              {activeTab > 0 && (
                <button
                  type="button"
                  className="branch-save-submit-btn btn btn-success"
                  onClick={previousHandler}
                  style={{
                    backgroundColor: "#1a7a73",
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    marginRight: "10px",
                  }}
                >
                  Previous
                </button>
              )}
              {tabsData.length - 1 === activeTab && (
                <>
                  <button
                    type="submit"
                    className="branch-save-submit-btn btn btn-success"
                    onClick={() => {
                      console.log("Submit button clicked");
                      console.log("Form errors:", errors);
                      console.log("Active tab:", activeTab);
                      console.log("Schema for tab:", schema[activeTab]);
                      console.log("Form values:", getValues());
                      console.log("User profile:", userProfile);
                    }}
                    style={{
                      backgroundColor: "#1a7a73",
                      fontFamily: "Poppins",
                      fontSize: "14px",
                      marginRight: "10px",
                    }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? t(edit ? "Updating" : "Submitting") + "..."
                      : t(edit ? "Update" : "saveAndSubmit")}
                  </button>
                  {!edit && (
                    <button
                      type="button"
                      className="branch-save-submit-btn btn btn-success"
                      onClick={handleCancelClick}
                    >
                      Cancel
                    </button>
                  )}
                </>
              )}
              {tabsData.length - 1 !== activeTab && (
                <button
                  type="button"
                  className="branch-save-submit-btn btn btn-success"
                  onClick={saveTabDataHandler}
                  style={{
                    backgroundColor: "#1a7a73",
                    fontFamily: "Poppins",
                    fontSize: "14px",
                  }}
                >
                  Save & Next
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Existing Cancel Modal */}
      <Modal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm</Modal.Title>
        </Modal.Header>
        <Modal.Body>Would you like to clear the input values?</Modal.Body>
        <Modal.Footer>
          <button
            className="btn"
            style={{
              backgroundColor: "lightgray",
              color: "#000",
              marginRight: "10px",
            }}
            onClick={() => setShowCancelModal(false)}
          >
            No
          </button>
          <button
            className="btn"
            style={{
              backgroundColor: "green",
              color: "#fff",
            }}
            onClick={handleConfirmCancel}
          >
            Yes
          </button>
        </Modal.Footer>
      </Modal>

      {/* NEW: Super Admin Confirmation Modal */}
      <Modal
        show={showSuperAdminModal}
        onHide={handleSuperAdminCancel}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Role Update</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This role update will enable Web Access and will be applied to all
          mapped instances. Do you want to proceed?
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn"
            style={{
              backgroundColor: "lightgray",
              color: "#000",
              marginRight: "10px",
            }}
            onClick={handleSuperAdminCancel}
            disabled={isSubmitting}
          >
            No
          </button>
          <button
            className="btn"
            style={{
              backgroundColor: "#1a7a73",
              color: "#fff",
            }}
            onClick={handleSuperAdminConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Processing..." : "Yes, Proceed"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RoleRegistrationForm;
